import { InternalGetInternalSessionsExportData, LifeglovUsage, Patient, SessionSummary } from '@bioliberty/lifemind';
import { getQueryCacheKey } from '../util';
import { downloadExportResponse } from './export-download';
import { tms } from './tms';

interface InternalDashboardQueryParams {
  tenantIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

interface InternalSurveyResponsesQueryParams {
  tenantIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

interface SurveySummary {
  questionSummaries: Record<string, Record<string, SurveyAnswerSummary>>;
}

interface SurveyAnswerSummary {
  questionId: number;
  count: number;
  percentage: number;
}

interface SurveyResponseDetailsQueryParams {
  questionId: number;
  answerValue: number;
  tenantIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export interface SurveyRespondent {
  patientId: number;
  patientName: string;
  clinicName: string;
}

export interface SurveyResponseDetails {
  questionId: number;
  answerValue: number;
  count: number;
  percentage: number;
  respondents: SurveyRespondent[];
}

interface InternalDashboardQueryResult {
  latestLifeglovUsage: Record<string, LifeglovUsage>;
  sessions: SessionSummary[];
  patients: Patient[];
}

const api = tms.injectEndpoints({
  endpoints: (builder) => ({
    getInternalDashboard: builder.query<InternalDashboardQueryResult, InternalDashboardQueryParams>({
      query: (params) => ({
        url: `internal/dashboard`,
        method: 'GET',
        params: {
          ...params,
          tenantIds: params.tenantIds?.join(',')
        }
      }),
      providesTags: (_result, _error, params) => [{ type: 'Internal', id: getQueryCacheKey(params, 'dashboard') }]
    }),
    getInternalSessionsExport: builder.mutation<void, InternalGetInternalSessionsExportData['query']>({
      query: (params) => ({
        url: `internal/export-sessions`,
        method: 'GET',
        params,
        responseHandler: downloadExportResponse,
        cache: 'no-store'
      })
    }),
    getSurveyResponses: builder.query<SurveySummary, InternalSurveyResponsesQueryParams>({
      query: (params) => ({
        url: `internal/survey-responses`,
        method: 'GET',
        params: {
          subtenantIds: params.tenantIds?.join(','),
          dateFrom: params.dateFrom,
          dateTo: params.dateTo
        }
      }),
      providesTags: (_result, _error, params) => [{ type: 'Internal', id: getQueryCacheKey(params, 'survey-response') }]
    }),
    getSurveyResponseDetails: builder.query<SurveyResponseDetails, SurveyResponseDetailsQueryParams>({
      query: (params) => ({
        url: `internal/survey-response-details`,
        method: 'GET',
        params: {
          questionId: params.questionId,
          answerValue: params.answerValue,
          subtenantIds: params.tenantIds?.join(','),
          dateFrom: params.dateFrom,
          dateTo: params.dateTo
        }
      }),
      providesTags: (_result, _error, params) => [
        { type: 'Internal', id: getQueryCacheKey(params, 'survey-response-details') }
      ]
    })
  }),
  overrideExisting: false
});

export const {
  useGetInternalDashboardQuery,
  useGetInternalSessionsExportMutation,
  useGetSurveyResponsesQuery,
  useGetSurveyResponseDetailsQuery
} = api;
